/* Copyright 2026 Element Creations Ltd.
SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial */
import type { MatrixClient } from "matrix-js-sdk";
import { backendFetch } from "../utils/backend";
import { guestOrigin, joinBrowserChallenge } from "./joinSession";

export async function joinAccountCommand(
  command: "sync" | "clear",
  userId: string,
  signal: AbortSignal,
  code?: string,
): Promise<void> {
  const origin = guestOrigin();
  return new Promise((resolve, reject) => {
    if (signal.aborted) { reject(new DOMException("Cancelled", "AbortError")); return; }
    const frame = document.createElement("iframe"), nonce = crypto.randomUUID();
    frame.hidden = true;
    frame.title = "Account session";
    frame.src = origin + "/auth/bridge";
    const finish = (error?: Error): void => {
      clearTimeout(timer); frame.remove();
      window.removeEventListener("message", message);
      signal.removeEventListener("abort", abort);
      if (error) reject(error); else resolve();
    };
    const abort = (): void => finish(new DOMException("Cancelled", "AbortError"));
    const hello = (): void => frame.contentWindow?.postMessage({type:"element-join-account",command,userId,code,nonce},origin);
    const message = (event: MessageEvent): void => {
      if(event.origin!==origin || event.source!==frame.contentWindow)return;
      if(event.data?.type==="element-join-ready") { hello(); return; }
      if(event.data?.type==="element-join-account-result" && event.data.nonce===nonce)
        finish(event.data.success ? undefined : new Error("Account synchronization unavailable"));
    };
    const timer = window.setTimeout(()=>finish(new Error("Account synchronization timed out")),8000);
    window.addEventListener("message",message);signal.addEventListener("abort",abort,{once:true});
    frame.onload=hello;document.body.append(frame);
  });
}

export async function syncJoinAccount(client: MatrixClient, signal: AbortSignal): Promise<void> {
  const me=await (await backendFetch(client,"/api/me",{signal})).json();
  if(me.userId!==client.getUserId() || !["owner","organizer"].includes(me.role))return;
  const receiver=await joinBrowserChallenge(guestOrigin(),signal);
  // Reuse the current cabinet pair even if join already reports the same user:
  // that browser could still have a device paired to an older cabinet login.
  // Exchange does not create a Matrix device; identical credentials are a no-op.
  const response=await backendFetch(client,"/api/auth/join-session/link",{
    method:"POST",headers:{"Content-Type":"application/json"},
    body:JSON.stringify({sessionOnly:true,challenge:receiver.challenge}),signal,
  });
  const {code}=await response.json();
  await joinAccountCommand("sync",client.getUserId()!,signal,code);
}
